import { useEffect, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import styled from 'styled-components';
import { usePublicContentService } from '~/api/hooks/usePublicContentService';
import CardGrid from '~/components/CardGrid';
import * as LSTVGlobals from '~/globals';

const shallowCompare = (obj1, obj2) =>
  Object.keys(obj1).length === Object.keys(obj2).length && Object.keys(obj1).every((key) => obj1[key] === obj2[key]);

const LoaderContainer = styled.div`
  margin: 16px 0 0 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentGrid = ({
  /**
   * Initial content, useful when populating the grid from SSR.
   * Will cause contentSearch to not be called on mount.
   */
  content = null,
  contentType = null,
  contentSearchType = null,
  fixedContentItems = null,
  contentSortMethod = null,
  limitToBusinessRoles = null,
  limitToBusinessRoleCapacity = null,
  limitToLocations = null,
  businessLocationScope = null,
  authorSlug = null,
  forceChangeCards = null,
  size = 8,
  verbosity = 'full',
  fixedItems = null,
  options = {},
  title = null,
  containerMode = 'grid',
  searchItems = null,
  /**
   * This callback is called if and when a server request has been made with the
   * total number of search results, a number.
   */
  onDataReady = null,
  payload = null,
  excludeItems = null,
  offset = 0,
  numColumns = null,
}) => {
  const { contentSearch, cancel: cancelContentSearch } = usePublicContentService();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const searchParams: any = {
    content_type: contentType === 'vibe' ? 'tag' : contentType,
    content_search_type: contentSearchType,
    search_items: searchItems,
    fixed_content_items: fixedContentItems,
    content_sort_method: contentSortMethod,
    exclude_items: excludeItems,
    min_weight_videos: 1,
    // makes sure we retrieve full objects rather than just slugs
    // that previously the card grid would then use to make individual queries
    verbosity: 'card',
  };

  if (limitToBusinessRoles) {
    searchParams.limit_to_business_roles = limitToBusinessRoles;
  }

  if (limitToBusinessRoleCapacity) {
    searchParams.limit_to_business_role_capacity = limitToBusinessRoleCapacity;
  }

  if (!limitToBusinessRoles && !limitToBusinessRoleCapacity) {
    // if no limits, we can assume that this is the catch-all wedding-vendors directory
    // https://app.clubhouse.io/lovestoriestv/story/185/remove-dress-designers-within-vendors
    searchParams.exclude_business_roles = 'dress-designer';
  }

  // Location filtering
  if (limitToLocations) {
    searchParams['limit_to_locations'] = limitToLocations;
    searchParams['business_location_scope'] = 'worked_or_based_at';
  }

  if (businessLocationScope) {
    searchParams['business_location_scope'] = businessLocationScope;
    searchParams['limit_to_locations'] = searchItems;
    searchParams['search_items'] = null;
  }

  if (authorSlug) {
    searchParams['author_slug'] = authorSlug;
  }

  const [data, setData] = useState<{
    content: any[];
    /**
     * Params that were used to retrieve `content` except `offset` and `size`.
     */
    searchParams: any;
    /**
     * Offset of the first item in `content`.
     */
    offset: number;
    noMoreItems: boolean;
  }>({
    content: content ?? [],
    searchParams,
    offset,
    noMoreItems: false,
  });

  const contentValid = shallowCompare(data.searchParams, searchParams) && data.offset === offset;

  useEffect(() => {
    if (
      !error &&
      !loading &&
      // Meaning: if search params have changed or we need more items.
      (!contentValid || (data.content.length < size && !data.noMoreItems)) &&
      // Not sure if we need/use the rest of this condition.
      contentType !== LSTVGlobals.CONTENT_GRID_CONTENT_TYPE_PHOTO &&
      !fixedItems
    ) {
      const numValidItems = contentValid ? data.content.length : 0;
      const maxResultSize = size - numValidItems;
      setLoading(true);
      contentSearch({ ...searchParams, offset: offset + numValidItems, size: maxResultSize }).then(
        ({ result, success, scope }) => {
          if (success === false) {
            setError(true);
            setLoading(false);
          } else {
            const newContent = [...(contentValid ? data.content : []), ...result];
            setData({
              content: newContent,
              searchParams,
              offset,
              noMoreItems: result.length < maxResultSize,
            });
            setError(false);
            setLoading(false);
            onDataReady?.(scope ? scope.total : 0);
          }
        }
      );
    }
  });

  useEffect(() => {
    return () => {
      cancelContentSearch();
    };
  }, []);

  return (
    <>
      {contentValid && data.content.length > 0 && (
        <CardGrid
          forceChangeCards={forceChangeCards}
          title={title}
          payload={payload}
          numCards={size + offset}
          content={data.content}
          fixedItems={fixedItems}
          cardType={contentType}
          verbosity={verbosity}
          numColumns={numColumns}
          {...options}
          containerMode={containerMode}
        />
      )}
      <LoaderContainer>
        {error ? (
          <p>There was a network error</p>
        ) : (
          <BeatLoader size={24} color={LSTVGlobals.LSTV_YELLOW} loading={loading} />
        )}
      </LoaderContainer>
    </>
  );
};

export default ContentGrid;
